import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import { Loader2, X, Plus } from "lucide-react"
import { useNavigate } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { Input } from "@/modules/shared/ui/components/input"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Button } from "@/modules/shared/ui/components/button"
import { DatePicker } from "@/modules/shared/ui/components/date-picker"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import type { EmployeeDto } from "@/modules/onboarding/employee/dtos"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"
import type { RootState, AppDispatch } from "@/store"
import { fetchWorkflows, selectWorkflows, selectWorkflowsState } from "@/modules/onboarding/workflows/store/workflowSlice"

const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const schema = z.object({
  offboardingReason: z.string().min(1, "Reason is required"),
  lastWorkingDate: z.date({ message: "Last working date is required" })
    .refine((date) => date >= startOfToday(), "Last working date cannot be in the past"),
  workflowTemplateIds: z.array(z.string()).optional(),
  comments: z.string().optional(),
  requiredDocuments: z.array(z.object({
    documentName: z.string(),
    employeeEditable: z.boolean(),
  })).optional(),
})

type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: EmployeeDto
  onSuccess?: () => void
}

export function StartOffboardingDialog({ open, onOpenChange, employee, onSuccess }: Props) {
  const organizationId = useSelector((s: RootState) => selectCurrentOrganizationId(s))
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const workflows = useSelector(selectWorkflows)
  const loadingWorkflows = useSelector((s: RootState) => selectWorkflowsState(s).isLoading)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentDocument, setCurrentDocument] = useState("")

  useEffect(() => {
    if (open) dispatch(fetchWorkflows())
  }, [open, dispatch])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      offboardingReason: "",
      workflowTemplateIds: [],
      comments: "",
      requiredDocuments: [],
    },
  })

  const handleAddDocument = () => {
    const doc = currentDocument.trim()
    const current = form.getValues("requiredDocuments") || []
    if (doc && !current.some(d => d.documentName === doc)) {
      form.setValue("requiredDocuments", [...current, { documentName: doc, employeeEditable: true }])
      setCurrentDocument("")
    }
  }

  const handleRemoveDocument = (index: number) => {
    const current = form.getValues("requiredDocuments") || []
    form.setValue("requiredDocuments", current.filter((_, i) => i !== index))
  }

  const handleToggleEditable = (index: number) => {
    const current = form.getValues("requiredDocuments") || []
    const updated = [...current]
    updated[index] = { ...updated[index], employeeEditable: !updated[index].employeeEditable }
    form.setValue("requiredDocuments", updated)
  }

  const handleClose = () => {
    onOpenChange(false)
    form.reset()
    setCurrentDocument("")
  }

  const handleSubmit = async (values: FormValues) => {
    if (!organizationId) {
      toast.error("No organisation selected")
      return
    }

    setIsSubmitting(true)
    try {
      const workflowTemplateIds = values.workflowTemplateIds?.filter(Boolean) ?? []
      const requiredDocuments = values.requiredDocuments?.filter(d => d.documentName) ?? []
      await offboardingService.adminInitiate(organizationId, {
        employeeId: employee.id,
        offboardingReason: values.offboardingReason,
        lastWorkingDate: values.lastWorkingDate.toISOString(),
        ...(workflowTemplateIds.length > 0 && { workflowTemplateIds }),
        ...(values.comments && { comments: values.comments }),
        ...(requiredDocuments.length > 0 && { requiredDocuments }),
      })
      toast.success(`Offboarding started for ${employee.firstName} ${employee.lastName}`)
      handleClose()
      onSuccess?.()
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Failed to start offboarding. Please try again."
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-lg p-0 max-h-[90vh] overflow-y-auto bg-card">
        <div className="p-6 border-b border-border">
          <DialogHeader className="gap-1">
            <DialogTitle className="text-2xl font-bold">Start Offboarding</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Initiate the offboarding process for{" "}
              <span className="font-medium text-foreground">
                {employee.firstName} {employee.lastName}
              </span>
            </DialogDescription>
          </DialogHeader>
        </div>

        <Form {...form}>
          <form id="offboarding-form" onSubmit={form.handleSubmit(handleSubmit)} className="p-6 space-y-5">

            {/* Offboarding Details */}
            <div>
              <h3 className="text-base font-semibold text-foreground">Offboarding Details</h3>
              <p className="text-sm text-muted-foreground mt-1">Provide the reason and timeline for offboarding.</p>
              <div className="mt-4 space-y-4">
                <FormField
                  control={form.control}
                  name="offboardingReason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Offboarding Reason <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Resignation, Termination, Retirement" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastWorkingDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Working Date <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ? new Date(field.value) : undefined}
                          onChange={field.onChange}
                          placeholder="Pick last working date"
                          disabled={{ before: startOfToday() }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="comments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Comments <span className="text-muted-foreground text-xs">(optional)</span></FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="e.g. Please ensure all assets are returned before last working day"
                          rows={3}
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Workflow */}
            <div>
              <h3 className="text-base font-semibold text-foreground">Workflow</h3>
              <p className="text-sm text-muted-foreground mt-1">Optionally auto-start a workflow to manage offboarding tasks.</p>
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="workflowTemplateIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Auto-Start Workflows (Optional)</FormLabel>
                      {workflows && workflows.length > 0 ? (
                        <>
                          <Select
                            value=""
                            onValueChange={(workflowId) => {
                              if (workflowId && !field.value?.includes(workflowId))
                                field.onChange([...(field.value || []), workflowId])
                            }}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={loadingWorkflows ? "Loading..." : "Select workflows to auto-start"}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-card">
                              {workflows.map((workflow) => (
                                <SelectItem
                                  className="hover:bg-muted cursor-pointer"
                                  key={workflow.id}
                                  value={workflow.id}
                                >
                                  {workflow.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {field.value && field.value.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {field.value.map((id) => {
                                const workflow = workflows.find((w) => w.id === id)
                                return workflow ? (
                                  <div
                                    key={id}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm"
                                  >
                                    <span>{workflow.name}</span>
                                    <button
                                      type="button"
                                      aria-label={`Remove ${workflow.name}`}
                                      onClick={() =>
                                        field.onChange(field.value?.filter((wId) => wId !== id) || [])
                                      }
                                      className="hover:text-primary/70"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : null
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="rounded-md border border-dashed border-border p-4 bg-muted">
                          <p className="text-sm text-muted-foreground">
                            You don't have any workflow templates yet. Create one to automate offboarding steps.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={() => navigate("/onboarding/workflows")}
                          >
                            Go to Workflow Templates
                          </Button>
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Required Documents */}
            <div>
              <h3 className="text-base font-semibold text-foreground">Required Documents</h3>
              <p className="text-sm text-muted-foreground mt-1">List documents the employee must submit before their last day.</p>
              <div className="mt-4">
                <FormField
                  control={form.control}
                  name="requiredDocuments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Documents Needed from Employee <span className="text-muted-foreground text-xs">(optional)</span></FormLabel>
                      <div className="flex gap-2">
                        <Input
                          value={currentDocument}
                          onChange={(e) => setCurrentDocument(e.target.value)}
                          placeholder="e.g. Resignation Letter, Equipment Return Form"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault()
                              handleAddDocument()
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleAddDocument}
                          disabled={!currentDocument.trim()}
                          className="shrink-0"
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </Button>
                      </div>
                      {field.value && field.value.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {field.value.map((doc, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-muted p-3 rounded-md border border-border"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <span className="text-sm text-foreground">{doc.documentName}</span>
                                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                  <Checkbox
                                    checked={doc.employeeEditable}
                                    onCheckedChange={() => handleToggleEditable(index)}
                                  />
                                  Employee Editable
                                </label>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-label={`Remove ${doc.documentName}`}
                                onClick={() => handleRemoveDocument(index)}
                                className="h-6 w-6 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

          </form>
        </Form>

        <div className="sticky bottom-0 bg-card border-t border-border p-4">
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" type="button" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="offboarding-form"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                "Start Offboarding"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
